import React, { useEffect, useMemo, memo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useFormik, FormikHelpers } from 'formik';
import * as Yup from 'yup';
import { useCreateMutation, useDetailQuery } from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { handleFormErrors } from 'utils/formErrorHandler';
import { fmt } from '../../../../../utils/common';
import { Adjustment } from '@/types/adjustment.types';

// ─── Types ────────────────────────────────────────────────────────────────────

type ChangeType = 'QuantityChanged' | 'PriceChanged' | 'DiscountChanged' | 'ItemRemoved';

interface AdjustmentItemValues {
  purchaseInvoiceItemId: string;
  shopItemId: string;
  shopItemName: string;
  originalQuantity: number;
  originalUnitPrice: number;
  originalDiscount: number;
  quantity?: number;
  purchasePricePerUnit?: number;
  quantityDelta: number;
  priceDelta: number;
  discountDelta: number;
  removed: boolean;
  notes?: string;
}

interface CreateAdjustmentFormValues {
  reason: string;
  items: AdjustmentItemValues[];
}

interface InvoiceItem {
  id: string;
  shopItemId: string;
  shopItem?: { name: string };
  quantity: number;
  netValue: number;
  purchasePricePerUnit: number;
  discountPercentage: number;
  taxAmount: number;
  batchNumber: string;
  manufacturerName?: string;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  totalAmount: number;
  items: InvoiceItem[];
  adjustments?: Adjustment[];
  status: string;
}

// ─── Design tokens (scoped CSS-in-JS style) ───────────────────────────────────

const T = {
  ink: '#0d0f12',
  ink2: '#374151',
  ink3: '#6b7280',
  ink4: '#9ca3af',
  surface: '#ffffff',
  surface2: '#f9fafb',
  surface3: '#f3f4f6',
  border: '#e5e7eb',
  border2: '#d1d5db',
  accent: '#2563eb',
  accentL: '#eff6ff',
  green: '#059669',
  greenL: '#ecfdf5',
  red: '#dc2626',
  redL: '#fef2f2',
  amber: '#d97706',
  amberL: '#fffbeb',
  radius: '8px',
  radiusLg: '12px',
  mono: "'JetBrains Mono', 'Fira Code', monospace",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function getItemChangeTypes(item: AdjustmentItemValues): ChangeType[] {
  if (item.removed) return ['ItemRemoved'];
  const types: ChangeType[] = [];
  if (item.quantityDelta !== 0) types.push('QuantityChanged');
  if (item.priceDelta !== 0) types.push('PriceChanged');
  if (item.discountDelta !== 0) types.push('DiscountChanged');
  return types;
}

// Aligned with backend effective-state logic:
// price change applies to all current qty; qty change at new effective price
function getItemEffect(item: AdjustmentItemValues): number {
  if (item.removed) return -(item.originalQuantity * item.originalUnitPrice);
  const effectiveQty = item.originalQuantity;
  const newPrice = item.originalUnitPrice + item.priceDelta;
  const priceEffect = effectiveQty * item.priceDelta;
  const qtyEffect = item.quantityDelta * newPrice;
  return priceEffect + qtyEffect - item.discountDelta;
}

const TYPE_META: Record<ChangeType, { label: string; color: string; bg: string }> = {
  QuantityChanged: { label: 'Qty change', color: T.accent, bg: T.accentL },
  PriceChanged: { label: 'Price change', color: T.green, bg: T.greenL },
  DiscountChanged: { label: 'Discount change', color: T.amber, bg: T.amberL },
  ItemRemoved: { label: 'Item removal', color: T.red, bg: T.redL },
};

const validationSchema = Yup.object({
  reason: Yup.string()
    .trim()
    .max(500, 'Max 500 characters')
    .required('A reason is required for audit purposes'),
});

// ─── Primitive components ─────────────────────────────────────────────────────

const Tag: React.FC<{ type: ChangeType }> = ({ type }) => {
  const m = TYPE_META[type];
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: '2px 7px',
        borderRadius: 20,
        background: m.bg,
        color: m.color,
        display: 'inline-flex',
        alignItems: 'center',
        whiteSpace: 'nowrap',
        lineHeight: 1.6,
      }}
    >
      {m.label}
    </span>
  );
};

const DeltaInput: React.FC<{
  value: number;
  onChange: (v: number) => void;
  step?: number;
  disabled?: boolean;
  active?: boolean;
  prefix?: string;
}> = ({ value, onChange, step = 1, disabled, active, prefix }) => (
  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
    {prefix && (
      <span
        style={{
          position: 'absolute',
          left: 10,
          fontSize: 12,
          color: T.ink3,
          pointerEvents: 'none',
          userSelect: 'none',
        }}
      >
        {prefix}
      </span>
    )}
    <input
      type="number"
      step={step}
      disabled={disabled}
      value={value}
      onChange={(e) =>
        onChange(step === 1 ? parseInt(e.target.value) || 0 : parseFloat(e.target.value) || 0)
      }
      style={{
        width: '100%',
        padding: prefix ? '6px 10px 6px 22px' : '6px 10px',
        fontSize: 13,
        fontFamily: T.mono,
        border: `1px solid ${active ? T.accent : T.border}`,
        borderRadius: T.radius,
        background: disabled ? T.surface3 : T.surface,
        color: disabled ? T.ink4 : value !== 0 ? T.accent : T.ink2,
        fontWeight: value !== 0 ? 600 : 400,
        outline: 'none',
        transition: 'border-color .15s',
        WebkitAppearance: 'none',
      }}
      onFocus={(e) => {
        if (!disabled) e.target.style.borderColor = T.accent;
      }}
      onBlur={(e) => {
        e.target.style.borderColor = active ? T.accent : T.border;
      }}
    />
  </div>
);

const Divider = () => <div style={{ height: 1, background: T.border, margin: '0 0 20px' }} />;

// ─── Shimmer skeleton ─────────────────────────────────────────────────────────

const Skeleton: React.FC<{ w?: string; h?: number }> = ({ w = '100%', h = 14 }) => (
  <div
    style={{
      width: w,
      height: h,
      borderRadius: 6,
      background: 'linear-gradient(90deg, #f3f4f6 25%, #e5e7eb 50%, #f3f4f6 75%)',
      backgroundSize: '400% 100%',
      animation: 'shimmer 1.4s infinite linear',
    }}
  />
);

// ─── Item row ─────────────────────────────────────────────────────────────────

const ItemRow: React.FC<{
  invoiceItem: InvoiceItem;
  index: number;
  formik: any;
}> = ({ invoiceItem, index, formik }) => {
  const item: AdjustmentItemValues = formik.values.items[index];
  const changeTypes = getItemChangeTypes(item);
  const effect = getItemEffect(item);
  const isRemoved = item.removed;
  const hasChange = changeTypes.length > 0;

  const set = (field: string, value: any) => formik.setFieldValue(`items.${index}.${field}`, value);

  const newQty = invoiceItem.quantity + item.quantityDelta;
  const newPrice = invoiceItem.purchasePricePerUnit + item.priceDelta;
  const newDiscount = invoiceItem.discountPercentage + item.discountDelta;

  return (
    <div
      style={{
        border: `1px solid ${isRemoved ? '#fca5a5' : hasChange ? '#bfdbfe' : T.border}`,
        borderRadius: T.radiusLg,
        padding: '16px 18px',
        background: isRemoved ? '#fff5f5' : hasChange ? '#f0f7ff' : T.surface,
        transition: 'all .2s',
        marginBottom: 10,
      }}
    >
      {/* Row header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          marginBottom: 14,
          gap: 12,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: T.ink, marginBottom: 2 }}>
            {invoiceItem.shopItem?.name ?? '—'}
          </div>
          <div style={{ fontSize: 12, color: T.ink4, fontFamily: T.mono }}>
            Batch: {invoiceItem.batchNumber}
            {invoiceItem.manufacturerName && <span> · {invoiceItem.manufacturerName}</span>}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          {changeTypes.length === 0 ? (
            <span style={{ fontSize: 12, color: T.ink4 }}>No changes</span>
          ) : (
            changeTypes.map((t) => <Tag key={t} type={t} />)
          )}
          <button
            type="button"
            onClick={() => {
              if (isRemoved) {
                set('removed', false);
              } else {
                set('removed', true);
                set('quantityDelta', 0);
                set('priceDelta', 0);
                set('discountDelta', 0);
              }
            }}
            title={isRemoved ? 'Undo removal' : 'Remove this item'}
            style={{
              border: `1px solid ${isRemoved ? '#fca5a5' : T.border}`,
              borderRadius: T.radius,
              background: isRemoved ? T.redL : T.surface2,
              color: isRemoved ? T.red : T.ink3,
              padding: '4px 10px',
              fontSize: 12,
              fontWeight: 500,
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 4,
              transition: 'all .15s',
              marginLeft: 4,
            }}
          >
            {isRemoved ? '↩ Undo' : '× Remove'}
          </button>
        </div>
      </div>

      {/* Original snapshot row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr) auto',
          gap: 12,
          marginBottom: 14,
        }}
      >
        {[
          { label: 'Original qty', value: `${invoiceItem.quantity} units` },
          { label: 'Original price', value: fmt(invoiceItem.purchasePricePerUnit) },
          { label: 'Discount', value: `${invoiceItem.discountPercentage}%` },
          {
            label: 'Line total',
            value: fmt(invoiceItem.quantity * invoiceItem.purchasePricePerUnit),
          },
        ].map((f) => (
          <div key={f.label}>
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '.05em',
                color: T.ink4,
                marginBottom: 3,
              }}
            >
              {f.label}
            </div>
            <div style={{ fontSize: 13, fontFamily: T.mono, color: T.ink2 }}>{f.value}</div>
          </div>
        ))}
      </div>

      {/* Delta inputs row */}
      {!isRemoved && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          {/* Quantity delta */}
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '.05em',
                color: T.ink4,
                marginBottom: 5,
              }}
            >
              Qty delta
            </div>
            <DeltaInput
              value={item.quantityDelta}
              onChange={(v) => set('quantityDelta', v)}
              active={item.quantityDelta !== 0}
            />
            {item.quantityDelta !== 0 && (
              <div style={{ marginTop: 4, fontSize: 11, color: T.accent, fontFamily: T.mono }}>
                {invoiceItem.quantity} → <strong>{newQty}</strong> units
              </div>
            )}
            <div style={{ marginTop: 3, fontSize: 11, color: T.ink4 }}>+/– from current qty</div>
          </div>

          {/* Price delta */}
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '.05em',
                color: T.ink4,
                marginBottom: 5,
              }}
            >
              Price delta
            </div>
            <DeltaInput
              value={item.priceDelta}
              onChange={(v) => set('priceDelta', v)}
              step={0.01}
              prefix="Rs"
              active={item.priceDelta !== 0}
            />
            {item.priceDelta !== 0 && (
              <div style={{ marginTop: 4, fontSize: 11, color: T.green, fontFamily: T.mono }}>
                {fmt(invoiceItem.purchasePricePerUnit)} → <strong>{fmt(newPrice)}</strong>
              </div>
            )}
            <div style={{ marginTop: 3, fontSize: 11, color: T.ink4 }}>+/– per unit</div>
          </div>

          {/* Discount delta */}
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '.05em',
                color: T.ink4,
                marginBottom: 5,
              }}
            >
              Discount delta
            </div>
            <DeltaInput
              value={item.discountDelta}
              onChange={(v) => set('discountDelta', v)}
              step={0.01}
              active={item.discountDelta !== 0}
            />
            {item.discountDelta !== 0 && (
              <div style={{ marginTop: 4, fontSize: 11, color: T.amber, fontFamily: T.mono }}>
                {invoiceItem.discountPercentage}% → <strong>{newDiscount}%</strong>
              </div>
            )}
            <div style={{ marginTop: 3, fontSize: 11, color: T.ink4 }}>+/– percentage points</div>
          </div>
        </div>
      )}

      {/* Item effect footer */}
      {hasChange && (
        <div
          style={{
            marginTop: 12,
            paddingTop: 10,
            borderTop: `1px solid ${T.border}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 6,
          }}
        >
          <span style={{ fontSize: 12, color: T.ink4 }}>Effect on invoice:</span>
          <span
            style={{
              fontSize: 14,
              fontWeight: 700,
              fontFamily: T.mono,
              color: effect < 0 ? T.red : effect > 0 ? T.green : T.ink4,
            }}
          >
            {effect > 0 ? '+' : ''}
            {fmt(effect)}
          </span>
        </div>
      )}
    </div>
  );
};

// ─── Section wrapper ──────────────────────────────────────────────────────────

const Section: React.FC<{
  step: number;
  title: string;
  subtitle?: string;
  done?: boolean;
  children: React.ReactNode;
}> = ({ step, title, subtitle, done, children }) => (
  <div
    style={{
      background: T.surface,
      border: `1px solid ${T.border}`,
      borderRadius: T.radiusLg,
      marginBottom: 14,
      overflow: 'hidden',
    }}
  >
    <div
      style={{
        padding: '14px 20px',
        borderBottom: `1px solid ${T.border}`,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        background: T.surface2,
      }}
    >
      <div
        style={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          fontWeight: 700,
          background: done ? T.greenL : T.accentL,
          color: done ? T.green : T.accent,
          border: `1.5px solid ${done ? '#a7f3d0' : '#bfdbfe'}`,
        }}
      >
        {done ? '✓' : step}
      </div>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: T.ink }}>{title}</div>
        {subtitle && <div style={{ fontSize: 12, color: T.ink3, marginTop: 1 }}>{subtitle}</div>}
      </div>
    </div>
    <div style={{ padding: '18px 20px' }}>{children}</div>
  </div>
);

// ─── Summary stat card ────────────────────────────────────────────────────────

const StatCard: React.FC<{
  label: string;
  value: string;
  sub?: string;
  valueColor?: string;
}> = ({ label, value, sub, valueColor }) => (
  <div
    style={{
      background: T.surface2,
      border: `1px solid ${T.border}`,
      borderRadius: T.radius,
      padding: '12px 16px',
    }}
  >
    <div
      style={{
        fontSize: 10,
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '.06em',
        color: T.ink4,
        marginBottom: 5,
      }}
    >
      {label}
    </div>
    <div style={{ fontSize: 18, fontWeight: 700, fontFamily: T.mono, color: valueColor ?? T.ink }}>
      {value}
    </div>
    {sub && <div style={{ fontSize: 11, color: T.ink4, marginTop: 3 }}>{sub}</div>}
  </div>
);

// ─── Main component ───────────────────────────────────────────────────────────

const CreateAdjustment: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const navigate = useNavigate();

  const { data: invoice, isLoading } = useDetailQuery<Invoice>(
    API_ENDPOINTS.TENANT.PURCHASES.WITH_ID,
    { id: invoiceId! },
  );

  const { mutate: createAdjustment, isPending: creating } = useCreateMutation(
    API_ENDPOINTS.TENANT.PURCHASES.ADJUSTMENTS.replace(':id', invoiceId!),
  );

  const previousAdjTotal = invoice?.adjustments?.reduce((s, a) => s + a.netAmountDelta, 0) ?? 0;

  const formik = useFormik<CreateAdjustmentFormValues>({
    initialValues: {
      reason: '',
      items:
        invoice?.items.map((item) => ({
          purchaseInvoiceItemId: item.id,
          shopItemId: item.shopItemId,
          shopItemName: item.shopItem?.name ?? '',
          originalQuantity: item.quantity,
          originalUnitPrice: item.purchasePricePerUnit,
          originalDiscount: item.discountPercentage,
          quantityDelta: 0,
          priceDelta: 0,
          discountDelta: 0,
          removed: false,
        })) ?? [],
    },
    enableReinitialize: true,
    validationSchema,
    onSubmit: (values, helpers: FormikHelpers<CreateAdjustmentFormValues>) => {
      const changedItems = values.items.filter((i) => getItemChangeTypes(i).length > 0);

      if (changedItems.length === 0) {
        (helpers.setFieldError as any)('items', 'Make at least one change before saving.');
        return;
      }

      const payload = {
        reason: values.reason.trim(),
        items: changedItems.map((item) => ({
          purchaseInvoiceItemId: item.purchaseInvoiceItemId,
          shopItemId: item.shopItemId,
          originalQuantity: item.originalQuantity,
          originalUnitPrice: item.originalUnitPrice,
          adjustmentTypes: getItemChangeTypes(item).map(
            (t) =>
              ({
                QuantityChanged: 'QuantityChange',
                PriceChanged: 'PriceChange',
                DiscountChanged: 'DiscountChange',
                ItemRemoved: 'ItemRemoval',
              }[t]),
          ),
          quantityDelta: item.quantityDelta,
          priceDelta: item.priceDelta,
          discountDelta: item.discountDelta,
          notes: item.notes,
        })),
      };

      createAdjustment(payload, {
        onSuccess: () => onClose(),
        onError: (error: any) => handleFormErrors<CreateAdjustmentFormValues>(error, helpers),
      });
    },
  });

  const netAmountDelta = useMemo(
    () => formik.values.items.reduce((s, i) => s + getItemEffect(i), 0),
    [formik.values.items],
  );

  const hasChanges = useMemo(
    () => formik.values.items.some((i) => getItemChangeTypes(i).length > 0),
    [formik.values.items],
  );

  const hasReason = formik.values.reason.trim().length > 0;
  const canSave = hasReason && hasChanges;

  const effectiveAfterThis = (invoice?.totalAmount ?? 0) + previousAdjTotal + netAmountDelta;

  // ── Loading ────────────────────────────────────────────────────────────────

  if (isLoading || !invoice) {
    return (
      <div style={{ maxWidth: 900, margin: '0 auto', padding: '24px 16px' }}>
        <style>{`@keyframes shimmer{0%{background-position:-400px 0}100%{background-position:400px 0}}`}</style>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                background: T.surface,
                border: `1px solid ${T.border}`,
                borderRadius: T.radiusLg,
                padding: 20,
              }}
            >
              <Skeleton w="160px" h={12} />
              <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 8 }}>
                <Skeleton />
                <Skeleton w="70%" />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (invoice.status !== 'Posted') {
    return (
      <div style={{ textAlign: 'center', padding: '60px 24px' }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>⚠</div>
        <div style={{ fontSize: 16, fontWeight: 600, color: T.ink, marginBottom: 6 }}>
          Invoice cannot be adjusted
        </div>
        <div style={{ fontSize: 13, color: T.ink3, marginBottom: 20 }}>
          Only posted invoices can have adjustments. This invoice is currently{' '}
          <strong>{invoice.status}</strong>.
        </div>
        <button
          onClick={() => navigate(-1)}
          style={{
            padding: '8px 18px',
            fontSize: 13,
            fontWeight: 500,
            background: T.accent,
            color: '#fff',
            border: 'none',
            borderRadius: T.radius,
            cursor: 'pointer',
          }}
        >
          ← Go back
        </button>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <>
      <style>{`
        @keyframes shimmer {
          0%   { background-position: -400px 0 }
          100% { background-position:  400px 0 }
        }
        input[type=number]::-webkit-inner-spin-button,
        input[type=number]::-webkit-outer-spin-button { -webkit-appearance: none; margin: 0; }
        input[type=number] { -moz-appearance: textfield; }
        * { box-sizing: border-box; }
      `}</style>

      <div style={{ maxWidth: 900, margin: '0 auto', padding: '0 0 56px' }}>
        {/* ── Topbar ──────────────────────────────────────────────────────── */}
        <div
          style={{
            background: T.surface,
            border: `1px solid ${T.border}`,
            borderRadius: T.radiusLg,
            padding: '14px 20px',
            marginBottom: 14,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 12,
          }}
        >
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: T.ink }}>Create adjustment</div>
            <div style={{ fontSize: 12, color: T.ink3, marginTop: 2 }}>
              Invoice&nbsp;
              <span style={{ fontFamily: T.mono, fontWeight: 600, color: T.ink2 }}>
                {invoice.invoiceNumber}
              </span>
              &nbsp;·&nbsp;Original total:&nbsp;
              <span style={{ fontFamily: T.mono, fontWeight: 600, color: T.ink2 }}>
                {fmt(invoice.totalAmount)}
              </span>
              {(invoice.adjustments?.length ?? 0) > 0 && (
                <span
                  style={{
                    marginLeft: 8,
                    padding: '1px 8px',
                    borderRadius: 20,
                    fontSize: 11,
                    background: T.accentL,
                    color: T.accent,
                    fontWeight: 600,
                  }}
                >
                  {invoice.adjustments!.length} prior adjustment
                  {invoice.adjustments!.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '7px 14px',
                fontSize: 13,
                fontWeight: 500,
                border: `1px solid ${T.border}`,
                borderRadius: T.radius,
                background: T.surface,
                color: T.ink3,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
          </div>
        </div>

        {/* Immutability notice */}
        <div
          style={{
            padding: '10px 16px',
            marginBottom: 14,
            background: T.accentL,
            border: `1px solid #bfdbfe`,
            borderRadius: T.radius,
            fontSize: 12.5,
            color: '#1d4ed8',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <span style={{ fontSize: 15, flexShrink: 0 }}>ℹ</span>
          The original invoice is <strong>never modified</strong>. This adjustment creates new stock
          movement and ledger entries alongside the original record.
        </div>

        <form onSubmit={formik.handleSubmit} noValidate>
          {/* ── Step 1: Reason ─────────────────────────────────────────────── */}
          <Section
            step={1}
            title="Reason for adjustment"
            subtitle="Required for audit trail"
            done={hasReason}
          >
            <textarea
              id="reason"
              name="reason"
              rows={2}
              placeholder="e.g. Supplier delivered 10 units but invoice billed for 15. Adjusting quantity down by 5."
              value={formik.values.reason}
              onChange={formik.handleChange}
              // onBlur={formik.handleBlur}
              style={{
                width: '100%',
                padding: '9px 12px',
                fontSize: 13,
                border: `1px solid ${
                  formik.touched.reason && formik.errors.reason ? T.red : T.border
                }`,
                borderRadius: T.radius,
                background: T.surface,
                color: T.ink,
                resize: 'vertical',
                outline: 'none',
                fontFamily: 'inherit',
                lineHeight: 1.6,
              }}
              onFocus={(e) => {
                e.target.style.borderColor = T.accent;
              }}
              onBlur={(e) => {
                formik.handleBlur(e);
                e.target.style.borderColor =
                  formik.touched.reason && formik.errors.reason ? T.red : T.border;
              }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <span style={{ fontSize: 11.5, color: T.red }}>
                {formik.touched.reason && formik.errors.reason ? formik.errors.reason : ''}
              </span>
              <span style={{ fontSize: 11, color: T.ink4 }}>{formik.values.reason.length}/500</span>
            </div>
          </Section>

          {/* ── Step 2: Items ───────────────────────────────────────────────── */}
          <Section
            step={2}
            title="Line item corrections"
            subtitle="Edit only the fields that need changing — leave others at 0"
            done={hasChanges}
          >
            {/* All active change types at a glance */}
            {/* {hasChanges &&
              (() => {
                const seen = new Set<ChangeType>();
                formik.values.items.forEach((i) =>
                  getItemChangeTypes(i).forEach((t) => seen.add(t)),
                );
                return (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      flexWrap: 'wrap',
                      padding: '8px 12px',
                      marginBottom: 14,
                      background: T.greenL,
                      border: `1px solid #a7f3d0`,
                      borderRadius: T.radius,
                      fontSize: 12.5,
                      color: T.green,
                    }}
                  >
                    <span style={{ fontWeight: 600 }}>
                      {seen.size} correction type{seen.size > 1 ? 's' : ''} detected:
                    </span>
                    {Array.from(seen).map((t) => (
                      <Tag key={t} type={t} />
                    ))}
                    <span style={{ marginLeft: 'auto', fontSize: 11, color: T.ink4 }}>
                      Types are inferred from your edits automatically
                    </span>
                  </div>
                );
              })()} */}

            {formik.values.items.map((_, index) => (
              <ItemRow
                key={invoice.items[index]?.id}
                invoiceItem={invoice.items[index]}
                index={index}
                formik={formik}
              />
            ))}

            {(formik.errors as any).items && typeof (formik.errors as any).items === 'string' && (
              <div style={{ marginTop: 8, fontSize: 12.5, color: T.red }}>
                {(formik.errors as any).items}
              </div>
            )}
          </Section>

          {/* ── Step 3: Summary + submit ────────────────────────────────────── */}
          <Section
            step={3}
            title="Review and save"
            subtitle="Confirm the financial impact before saving"
          >
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
                gap: 10,
                marginBottom: 20,
              }}
            >
              <StatCard
                label="Original total"
                value={fmt(invoice.totalAmount)}
                sub="Before any adjustments"
              />
              {(invoice.adjustments?.length ?? 0) > 0 && (
                <StatCard
                  label="Prior adjustments"
                  value={`${previousAdjTotal >= 0 ? '+' : ''}${fmt(previousAdjTotal)}`}
                  sub={`${invoice.adjustments!.length} adjustment${
                    invoice.adjustments!.length > 1 ? 's' : ''
                  } applied`}
                  valueColor={
                    previousAdjTotal < 0 ? T.red : previousAdjTotal > 0 ? T.green : T.ink3
                  }
                />
              )}
              <StatCard
                label="This adjustment"
                value={`${netAmountDelta > 0 ? '+' : ''}${fmt(netAmountDelta)}`}
                sub={
                  netAmountDelta === 0
                    ? 'No changes made yet'
                    : netAmountDelta < 0
                    ? 'Invoice will decrease'
                    : 'Invoice will increase'
                }
                valueColor={netAmountDelta === 0 ? T.ink4 : netAmountDelta < 0 ? T.red : T.green}
              />
              <StatCard
                label="New effective total"
                value={fmt(effectiveAfterThis)}
                sub="After this adjustment"
                valueColor={T.ink}
              />
            </div>

            <Divider />

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 12,
                flexWrap: 'wrap',
              }}
            >
              {/* Inline hint */}
              <span style={{ fontSize: 12, color: T.ink4 }}>
                {!hasReason && !hasChanges && 'Complete steps 1 and 2 to save.'}
                {!hasReason && hasChanges && 'Add a reason above to enable saving.'}
                {hasReason && !hasChanges && 'Make at least one change to enable saving.'}
                {canSave && (
                  <span style={{ color: T.green }}>
                    ✓ Ready to save —{' '}
                    {formik.values.items.filter((i) => getItemChangeTypes(i).length > 0).length}{' '}
                    item
                    {formik.values.items.filter((i) => getItemChangeTypes(i).length > 0).length > 1
                      ? 's'
                      : ''}{' '}
                    will be adjusted
                  </span>
                )}
              </span>

              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  disabled={creating}
                  style={{
                    padding: '8px 16px',
                    fontSize: 13,
                    fontWeight: 500,
                    border: `1px solid ${T.border}`,
                    borderRadius: T.radius,
                    background: T.surface,
                    color: T.ink3,
                    cursor: 'pointer',
                  }}
                >
                  Discard
                </button>
                <button
                  type="submit"
                  disabled={creating || !canSave}
                  style={{
                    padding: '8px 22px',
                    fontSize: 13,
                    fontWeight: 600,
                    border: 'none',
                    borderRadius: T.radius,
                    cursor: canSave ? 'pointer' : 'not-allowed',
                    background: canSave ? T.accent : T.border,
                    color: canSave ? '#fff' : T.ink4,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 6,
                    transition: 'background .15s',
                    opacity: creating ? 0.7 : 1,
                  }}
                >
                  {creating ? (
                    <>
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          border: '2px solid #fff4',
                          borderTopColor: '#fff',
                          borderRadius: '50%',
                          animation: 'spin .7s linear infinite',
                          display: 'inline-block',
                        }}
                      />
                      Saving…
                    </>
                  ) : (
                    '↗ Save adjustment'
                  )}
                </button>
              </div>
            </div>
          </Section>
        </form>
      </div>
    </>
  );
};

export default memo(CreateAdjustment);
